import { useApolloClient } from "@apollo/client/react";
import { todayPT } from "@homebound/activesupport";
import { currentAuthToken } from "@homebound/auth-components";
import {
  BoundSelectField,
  Button,
  Css,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Palette,
  StaticField,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { Observer } from "mobx-react";
import { useState } from "react";
import { FileField } from "src/components";
import {
  BidContractPlanPricing_RevisionDocument,
  BidContractPlanPricing_RevisionFragment,
} from "src/components/bid-contracts/BidContractPlanPricingPage.generated";
import { baseDownloadUrl } from "src/context";
import { BidContractType } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  BidPackageDetailPageDocument,
  BidPackageDetailPage_BidContractRevisionFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { useCreateBidContractMutation } from "src/routes/developments/procurement/components/CreateDevelopmentContractButton.generated";
import { useSaveBidContractRevisionMutation } from "src/routes/developments/procurement/DevelopmentContractPage.generated";
import { ObjectConfig, required, useFormState } from "src/utils/formState";
import { openInSelf } from "src/utils/window";
import { UnitBasedBidsTableDocument } from "./UnitBasedBidsTable.generated";

type ImportBidModalProps = {
  requests: BidPackageDetailActiveRequestFragment[];
  bidPackageName: string;
  latestBidContractRevisions: Array<
    BidPackageDetailPage_BidContractRevisionFragment | BidContractPlanPricing_RevisionFragment
  >;
  bidPackageVersionId: string;
  isUnitBased?: boolean;
  tradePartnerId?: string;
};

export function ImportBidModal({
  bidPackageName,
  requests,
  latestBidContractRevisions,
  bidPackageVersionId,
  isUnitBased = true,
  tradePartnerId,
}: ImportBidModalProps) {
  const [errors, setErrors] = useState<string[]>([]);
  const [erroredUploadingXlsx, setErroredUploadingXlsx] = useState<boolean>(false);
  const [createdBidContractId, setCreatedBidContractId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { triggerNotice } = useSnackbar();
  const { closeModal } = useModal();
  const client = useApolloClient();
  const [createBidContract] = useCreateBidContractMutation();
  const [createBidContractRevision] = useSaveBidContractRevisionMutation();

  const tradePartners = requests.map((request) => request.tradePartner);
  const formState = useFormState({
    config: formConfig,
    init: { onlyOnce: true, input: { tradePartner: tradePartnerId ?? tradePartners[0].id } },
  });
  const onDownloadTemplate = () => {
    const params = new URLSearchParams({
      type: isUnitBased ? "unitBasedBidPackage" : "planBasedBidPackage",
      bidPackageRequestId: requests.find((r) => r.tradePartner.id === formState.tradePartner.value)?.id ?? "",
      ...(isUnitBased ? {} : { fullBid: "true" }),
    });
    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
  };

  async function onImport() {
    if (loading) return;
    setLoading(true);

    let bidContractId = requests.find((r) => r.tradePartner.id === formState.tradePartner.value)?.bidContract?.id;
    const bidContractRevisionId = latestBidContractRevisions.find(
      (bcr) => bcr.bidContract.tradePartner?.id === formState.tradePartner.value,
    )?.id;

    // If we have errored out when uploading the file, we don't want to create a new bid contract/revision.
    if (!erroredUploadingXlsx) {
      if (!bidContractId) {
        const { data } = await createBidContract({
          variables: {
            input: {
              tradePartnerId: formState.value.tradePartner,
              bidRequestId: requests.find((r) => r.tradePartner.id === formState.value.tradePartner)?.id,
              startDate: todayPT(),
              type: BidContractType.UnitBased,
            },
          },
          errorPolicy: "ignore",
        });

        bidContractId = data?.saveBidContract.bidContract.id;

        if (!bidContractId) {
          triggerNotice({
            message:
              "We couldn't create the bid contract for this trade, so the file wasn't uploaded. Please try again, and if it keeps happening, contact support with the project and trade details.",
          });
          setLoading(false);
          return;
        }
        setCreatedBidContractId(bidContractId);
      } else if (!bidContractRevisionId) {
        await createBidContractRevision({
          variables: { input: { bidContractId, bidPackageVersionId, startDate: todayPT() } },
          errorPolicy: "ignore",
        });
      }
    }

    const response = await fetch(
      `${baseDownloadUrl()}/xlsx?type=${isUnitBased ? "bidContractUnitBased" : "bidContractPlanBased"}&bidContractId=${createdBidContractId ?? bidContractId}`,
      { method: "POST", headers: { Authorization: `Bearer ${await currentAuthToken()}` }, body: formState.value.file },
    );

    if (response.status !== 200) {
      setErroredUploadingXlsx(true);
      // Backend may have identified errors in the xlsx (Like unfilled Total Cost columns or poorly-formatted numbers)
      // so pop them into the Errors array here.
      const { message } = await response.json();
      if (message) setErrors([message]);
      setLoading(false);
      return;
    }

    // using Apollo Client directly instead of prop drilling refetch to update cache
    await client.refetchQueries({
      include: [BidPackageDetailPageDocument, UnitBasedBidsTableDocument, BidContractPlanPricing_RevisionDocument],
    });
    triggerNotice({ message: "Data imported successfully" });

    closeModal();
  }

  return (
    <>
      <ModalHeader>Import Items: {bidPackageName}</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div>
            Import items from a{" "}
            <span css={Css.blue600.cursorPointer.$} data-testid="xlsxTemplateLink" onClick={onDownloadTemplate}>
              .xlsx template
            </span>
          </div>
          {!tradePartnerId && (
            <BoundSelectField
              field={formState.tradePartner}
              label="Select Trade:"
              options={tradePartners}
              // If we have already created a bid contract in the modal then errored out, prevent changing the trade partner.
              disabled={!!createdBidContractId}
            />
          )}

          <StaticField label="Upload Document">
            <div css={Css.df.fdc.$}>
              <div css={Css.df.fdr.aic.gap1.mb1.$}>
                <Icon icon="error" inc={2} color={Palette.Red400} />
                <span css={Css.xs2.$}>
                  New uploaded costs will override any existing cost entries for the selected trade
                </span>
              </div>

              <div css={Css.w100.mt("-24px").$}>
                <Observer>
                  {() => (
                    <FileField
                      label="Click or Drag to Upload"
                      allowedFileTypes="excel"
                      file={formState.file.value}
                      setFile={(file) => {
                        formState.set({ file });
                        setErrors([]);
                      }}
                      errors={errors}
                    />
                  )}
                </Observer>
              </div>
            </div>
          </StaticField>
        </div>
      </ModalBody>
      <ModalFooter>
        <Observer>
          {() => (
            <>
              <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
              <Button
                label="Import"
                disabled={
                  loading || (!formState.valid && "Select a trade and attach a file to upload.") || errors?.toString()
                }
                onClick={onImport}
              />
            </>
          )}
        </Observer>
      </ModalFooter>
    </>
  );
}

type FormValues = { tradePartner?: string | null; file?: File | null };

const formConfig: ObjectConfig<FormValues> = {
  tradePartner: { type: "value", rules: [required] },
  file: { type: "value", rules: [required] },
};
